import { reactive, computed, onBeforeMount } from '@vue/composition-api'
import Vue from 'vue'
import store from '@/store'
import router from '@/router'
import * as apiContract from '@/api/contract'
import * as apiPayment from '@/api/payment'
import { getLCRequestName, convertSettlementToPaymentClassification, isInvoicingInformationAvailable, isWebInvoiceIssuanceApprovalAvailable } from '@/logic/common/CommonPaymentMethod'

export const ContractAccountPaymentMethod = (props, context) => {

  // 選択肢でクレジットカードを判定するために使用するID
  // (クレジットカードは、「お支払い方法」欄では、一括と分割は位置項目として表示する)
  // ※このIDの値は、フロントの処理専用のIDである。
  const CONTRACT_CHOICE_ID_CREDIT = 901

  onBeforeMount(() => {
    paymentMethod.init()
  })

  const paymentMethod = reactive({
    // 申請する支払い方法のID
    paymentClassification: 0,

    // 審査中or審査完了の支払い方法のID
    // paymentClassificationは選択肢のチェック状態を保持する変数であり、「支払い方法取得API」のcheckの値を参照するが、
    // これは、審査中の支払い方法を知ることができない。
    // 審査中の支払い方法を判定するために、「申請情報取得API」の支払い方法を参照して、この変数に設定する
    examPaymentClassification: 0,

    // 現在設定している支払い方法のID
    defaultPayment: computed(() => store.state.contract.default_payment),

    // WEB会員ID
    webuser_id: '',

    enable_changed: false, // 変更可能状態(true:変更可 false:変更不可)
    authorization: false, // 自動決済認証状態(true:自動決済認証済み false:自動決済未認証)
    list: [], // 支払い方法一覧

    // エラーメッセージ
    error_paymentClassification: '',
    error_orderCode: '',

    // 状態コード (1:受付 2:審査中 3:有効 4:停止 8:却下 9:解約 97:契約削除 98:合併 99:退会)
    lcRequest: 0,

    // 申込認証コード
    offerCode: '',

    // 自動決済お申込み状態
    offerCodeRequestStatus: '',

    // 申込認証コード取得APIのレスポンスのお支払方法
    pay_method: 0,

    // Webセッション取得APIのステータス
    isWebSessionExist: false,

    // webSessionを取得したかどうか
    isWebSessionFetched: false,

    // webセッション取得APIのレスポンス(WEB会員ID)
    webuser_id_session: '',

    // ログイン・新規登録ボタンのURL
    loginBtnUrl: '/shop/signin?redirect_form=contract_information_pc',

    // 状態コードに対する名前
    lcRequestName: computed(() => {
      return getLCRequestName(paymentMethod.lcRequest)
    }),

    // 「ご請求案内」画面を使用できるか？
    isInvoicingInformationAvailable: computed(() => {
      return isInvoicingInformationAvailable()
    }),

    // 「WEB請求書発行（PDF）を有効にする」を使用できるか？
    isWebInvoiceIssuanceApprovalAvailable: computed(() => {
      return isWebInvoiceIssuanceApprovalAvailable()
    }),

    // (現在の)「お支払い方法」の欄に表示する文言
    currentPaymentName: computed(() => {
      let name = ''
      if (props.parentName === 'EstimateFinalIndex') {
        // お見積もり内容の確認画面の場合
        if (paymentMethod.authorization && paymentMethod.examPaymentClassification === 7) {
          // WEB請求書発行申請した場合は、受付や審査中でもお支払い方法は銀行振込とする。
          name = '銀行振込'
        } else {
          name = '審査中'
        }
      } else if (props.parentName === 'PCContract') {
        // 契約情報画面の場合
        const defaultPayment = store.state.contract.default_payment

        if (paymentMethod.authorization && paymentMethod.examPaymentClassification === 7) {
          // WEB請求書発行申請した場合は、受付や審査中でもお支払い方法は銀行振込とする。
          name = '銀行振込'
        } else {
          switch (defaultPayment) {
            case 1:
            case 2:
              name = 'クレジットカード'
              break
            case 3:
              name = '口座振替'
              break
            case 6:
              name = 'コンビニ支払い・郵便振替'
              break
            case 7:
              name = '銀行振込'
              break
            default:
              name = '審査中'
              break
          }
        }
      }
      return name
    }),

    // お支払方法登録の表示判定
    choosePaymentMethod: computed(() => {
      // クレジットカードを選択かつWEBログイン済み
      if (paymentMethod.isWebSessionExist && paymentMethod.paymentClassification === CONTRACT_CHOICE_ID_CREDIT) {
        return CONTRACT_CHOICE_ID_CREDIT
        // 領収書を選択かつWEBログイン済み
      } else if (paymentMethod.isWebSessionExist && paymentMethod.paymentClassification === 9) {
        return 9
        // クレジットカードまたは領収書かつWEBログイン済み
      } else if (!paymentMethod.isWebSessionExist && (paymentMethod.paymentClassification === CONTRACT_CHOICE_ID_CREDIT || paymentMethod.paymentClassification === 9)) {
        return 1
      } else {
        return 0
      }
    }),

    isLoaded: false, // ロード完了フラグ

    /**
     * 初期表示
     */
    init: () => {
      // コンテンツを更新
      paymentMethod.updateContents(null)
      paymentMethod.isWebSessionFetched = false
    },

    /**
     * コンテンツの再読み込み
     */
    reload: () => {
      // エリアをクリア
      paymentMethod.list = []
      paymentMethod.isWebSessionFetched = false

      // コンテンツを更新
      paymentMethod.updateContents(null)
    },

    /**
     * 自動決済認証済みの支払い方法（審査中、審査完了）か？
     * @returns 
     */
    isAuthorizedPaymentMethod: () => {
      if (
        paymentMethod.authorization && // 自動決済認証済み(WEBと連携済み)
        (
          paymentMethod.examPaymentClassification === 1 || // クレジットカード（一括）
          paymentMethod.examPaymentClassification === 2 || // クレジットカード（分割）
          paymentMethod.examPaymentClassification === 3 || // 口座振替
          paymentMethod.examPaymentClassification === 7 // 銀行振込
        )
      ) {
        return true
      } else {
        return false
      }
    },

    /**
     * 支払い方法の選択を表示するか？
     */
    dispChoices: computed(() => {
      if (props.parentName === 'EstimateFinalIndex') {
        // お見積もり内容の確認画面の場合

        // enable_changedは、同月に複数パターンの支払い方法を選択した申込をさせないための制限を行うためのフラグである
        // 無料トライアルに対しては対象申込が必ず当月に発生するのでロジックとしては当月登録済みになるためfalseになるため、
        // お見積もり内容の確認画面では、enable_changedの値は無視する

        if (paymentMethod.isAuthorizedPaymentMethod()) {
          // お見積り画面からWEB請求書発行の設定を行った後は、支払い方法の選択はさせない
          return false
        } else {
          return true
        }
      } else if (props.parentName === 'PCContract') {
        // 契約情報画面の場合
        return paymentMethod.enable_changed
      }
    }),

    /**
     * コンテンツを更新
     */
    updateContents: (callback) => {

      // 初期化
      paymentMethod.paymentClassification = 0
      paymentMethod.examPaymentClassification = 0
      paymentMethod.error_orderCode = ''

      if (props.parentName === 'EstimateFinalIndex') {
        paymentMethod.loginBtnUrl = '/shop/signin?redirect_form=confirm_estimate_token=' + store.state.auth.token
      }

      // 支払い方法取得APIをコール
      const params = {
      }
      paymentMethod.isLoaded = false

      apiPayment.fetchPaymentMethod(params)
        .then(response => {
          if (response) {
            const { data } = response
            const status = data.status

            if (status === 0) {
              paymentMethod.enable_changed = data.enable_changed
              paymentMethod.authorization = data.authorization

              const listWork = []

              const list = data.lists
              for (var i = 0; i < list.length; i++) {
                const id = parseInt(list[i].id)
                const name = list[i].name
                const check = list[i].check

                listWork.push({
                  id: id,
                  name: name
                })

                if (check) {
                  // 初期表示時にチェックする支払い方法を設定
                  paymentMethod.paymentClassification = id
                }

              }

              if (paymentMethod.paymentClassification === 0) {
                // 支払い方法が取得できない場合、クレジットカード/口座振替をデフォルトとして設定
                paymentMethod.paymentClassification = CONTRACT_CHOICE_ID_CREDIT
              }

              if (store.state.payment.paymentClassification === CONTRACT_CHOICE_ID_CREDIT || store.state.payment.paymentClassification === 9 || store.state.payment.paymentClassification === 6 || store.state.payment.paymentClassification === 7) {
                // もし決済方法が保存されていた場合は、そのラジオボタンをチェックした状態にする。
                paymentMethod.paymentClassification = store.state.payment.paymentClassification
              }

              if (!paymentMethod.authorization) {
                // 自動決済未認証の場合
                // 支払い方法取得API(SN-002)からは「6=振替・コンビニ、7=銀行振込」しか返ってこない。
                // また、クレジットカード(一括、分割)の選択肢は1つでまとめて表示する
  
                // その他の表示用の選択肢を追加
                listWork.unshift({
                  id: 9,
                  name: '銀行振込（WEB請求書）'
                })
                listWork.unshift({
                  id: CONTRACT_CHOICE_ID_CREDIT,
                  name: 'クレジットカード/口座振替'
                })
              }
              paymentMethod.list = listWork
              if (callback) {
                // コールバック
                callback()
              }
              if (paymentMethod.authorization) {
                // 申請情報を取得できるのは、authorization=true(WEB会員認証済み)の場合のみである
                // ※逆に、authorization=falseの場合は、申請情報取得APIにリクエストしても何も返らないため、リクエスト不要

                // 申請情報取得APIをコール
                const identificationCode = store.state.profile.identification_code
                const params2 = {
                  identification_code: identificationCode // 契約識別コード
                }
                apiPayment.fetchApplicationInfo(params2)
                  .then(response => {
                    if (response) {
                      const { data } = response
                      const status = data.status

                      if (status === 0) {
                        // Web会員ID
                        paymentMethod.webuser_id = data.webuser_id ?? ''

                        // リーガルコネクションの申請状況
                        const lcResult = data['2']

                        // 状態コード (1:受付 2:審査中 3:有効 4:停止 8:却下 9:解約 97:契約削除 98:合併 99:退会)
                        // ※審査中は「1:受付 or 2:審査中」、審査完了は「3:有効」である
                        const lcRequest = parseInt(lcResult.request)
                        paymentMethod.lcRequest = lcRequest

                        // 状態コードをストアに保持
                        store.dispatch('payment/setRequest', lcRequest)
                        
                        // 決済方法（1:クレジット 2:口座振替 9:WEB請求書発行（銀行振込））
                        const lcSettlement = parseInt(lcResult.settlement)
                        // 支払い方法（クレジットカードの場合のみ　1:一括　2:分割）
                        const lcPayment = parseInt(lcResult.payment)

                        // payment_classificationのID体系に変換して、変数examPaymentClassificationに設定
                        paymentMethod.examPaymentClassification = convertSettlementToPaymentClassification(lcSettlement, lcPayment)
                      }
                    }
                  })
              }
            }
          }
          // webセッション取得APIをコールする
          apiContract.getWebSession(params)
            .then(response => {
              const { data } = response
              const status = data.status
              if (status === 0) {
                paymentMethod.isWebSessionExist = true
                paymentMethod.webuser_id_session = data.web_mem_info.webuser_id
              } 
              const params = {
                webuser_id: paymentMethod.webuser_id_session
              }
              // 申込認証コード取得APIをコールする
              apiContract.getOfferCode(params)
                .then(response => {
                  const { data } = response
                  const status = data.status
                  if (status === 0) {
                    paymentMethod.offerCodeRequestStatus = data.request_status
                  }
                })
            })
        })
        .finally(() => {
          paymentMethod.isLoaded = true
        })
    },

    /**
     * 決済申請へ進むボタン押下時
     */
    onWebLoginCheckClicked: () => {
      // 初期表示でエラー表示させないため
      const params = {
      }
      apiContract.getWebSession(params).then(response => {
        paymentMethod.isWebSessionFetched = true
        if (response.data.status === 0) {
          paymentMethod.isWebSessionExist = true
          // ストアに選択した支払い方法に基づくIDを設定
          // 決済申請ボタンを押下した後に、押下前の選択を引き継ぐため。
          store.dispatch('payment/setPaymentClassification', paymentMethod.paymentClassification)

          paymentMethod.updateContents(null)
        } else {
          paymentMethod.isWebSessionExist = false
        }
      })
    },

    /**
     * ログインボタン押下時
     */
    onWebLoginClicked: () => {
      // ログイン画面に遷移させる前に、決済方法を保存しておく。
      // ログイン後にログイン前の決済方法を選択している状態にするため。
      store.dispatch('payment/setPaymentClassification', paymentMethod.paymentClassification)
    },

    /**
     * 設定ボタン押下時
     */
    onSettingButtonClicked: () => {
      paymentMethod.isWebSessionFetched = false

      // 入力チェック
      let isError = false

      if (paymentMethod.paymentClassification === 0 || paymentMethod.paymentClassification === '') {
        paymentMethod.error_paymentClassification = '必須項目です。'
        isError = true
      }

      if (isError) {
        // 入力エラーが存在した場合
        return false
      }

      if (props.parentName === 'EstimateFinalIndex') {
        // お見積もり内容の確認画面の場合
        // クレジットカードまたはWEB請求書の時
        if (paymentMethod.paymentClassification === CONTRACT_CHOICE_ID_CREDIT || paymentMethod.paymentClassification === 9) {
          // 次の画面のためにwebセッション取得APIのwebuser_idをストアに保存
          store.dispatch('estimateFinal/setWebuserId', paymentMethod.webuser_id_session)
          const params = {
            webuser_id: paymentMethod.webuser_id_session
          }
          // 申込認証コード取得APIをコールする
          apiContract.getOfferCode(params)
            .then(response => {
              const { data } = response
              const status = data.status
              if (status === 0) {
                store.dispatch('estimateFinal/setOfferCodeData', data)
              } else {
                paymentMethod.error_orderCode = data.message
                return false
              }

              // ストアに選択した支払い方法に基づくIDを設定
              store.dispatch('payment/setPaymentClassification', data.pay_method)

              // 次の画面に進む
              context.emit('submit')
            })
        } else {
          // ストアに選択した支払い方法に基づくIDを設定
          store.dispatch('payment/setPaymentClassification', paymentMethod.paymentClassification)

          // 次の画面に進む
          context.emit('submit')
        }

      } else if (props.parentName === 'PCContract') {
        // 契約情報画面の場合

        // ここでは選択肢のIDと支払い方法IDは同じ値
        let paymentClassification = paymentMethod.paymentClassification

        // ストアに選択した支払い方法に基づくIDを設定
        store.dispatch('payment/setPaymentClassification', paymentClassification)

        if (paymentMethod.paymentClassification === CONTRACT_CHOICE_ID_CREDIT || paymentMethod.paymentClassification === 9) {
          const params = {
            webuser_id: paymentMethod.webuser_id_session
          }
          // 申込認証コード取得APIをコールする
          apiContract.getOfferCode(params)
            .then(response => {
              const { data } = response
              const status = data.status
              if (status === 0) {
                paymentMethod.pay_method = data.pay_method
                paymentMethod.offerCode = data.request_code
                paymentClassification = paymentMethod.pay_method
                paymentMethod.onCallUpdateContract(paymentClassification)
              } else {
                paymentMethod.error_orderCode = data.message
                return false
              }
            })
        } else {
          paymentMethod.onCallUpdateContract(paymentClassification)
        }
      }    
    },

    onCallUpdateContract: (paymentClassification) => {
      // お支払方法がクレジットカード/口座振替または銀行振込（WEB請求書）の時
      if (paymentMethod.paymentClassification === CONTRACT_CHOICE_ID_CREDIT || paymentMethod.paymentClassification === 9) {
        const params = {
          webuser_id: paymentMethod.webuser_id_session,
          identification_code: store.state.profile.identification_code,
          settlement_type: '',
          request_code: paymentMethod.offerCode,
          check: 2
        }

        if (paymentMethod.pay_method === 2) {
          params.settlement_type = 1
        } else if (paymentMethod.pay_method === 3) {
          params.settlement_type = 2
        } else {
          params.settlement_type = paymentMethod.pay_method
        }

        if (paymentMethod.pay_method === 1) {
          params.payment_type = 1
        } else if (paymentMethod.pay_method === 2) {
          params.payment_type = 2
        }
        // WEB会員認証APIをコールする
        apiContract
          .authWebUser(params)
          .then(response => {
            const { data } = response
            const status = data.status
            if (status === 0) {
              // 契約情報変更APIをコール
              const params2 = {
                payment_method: paymentClassification
              }

              // 申込認証コード取得APIのpay_method == 9の時7を設定する
              if (paymentMethod.pay_method === 9) {
                params2.payment_method = 7
              }

              store.dispatch('common/showLoading')
              apiContract
                .updateContract(params2)
                .then(response => {
                  const data = response.data
                  if (data.status === 0) {
                    const callback = () => {
                      // 更新完了のトーストを表示
                      store.dispatch('common/showToast', { text: '既定の支払い方法を更新しました。' })
                    }
                    // コンテンツを更新
                    paymentMethod.updateContents(callback) 
                  }
                })
                .finally(() => {
                  store.dispatch('common/hideLoading')
                })    
            }
          })
      } else {
        // 契約情報変更APIをコール
        const params2 = {
          payment_method: paymentClassification
        }

        store.dispatch('common/showLoading')
        apiContract
          .updateContract(params2)
          .then(response => {

            const data = response.data

            if (data.status === 0) {
              const callback = () => {
                // 更新完了のトーストを表示
                store.dispatch('common/showToast', { text: '既定の支払い方法を更新しました。' })
              }
              // コンテンツを更新
              paymentMethod.updateContents(callback) 
            }
          })
          .finally(() => {
            store.dispatch('common/hideLoading')
          })
      }
    }
  })

  return {
    paymentMethod,
    CONTRACT_CHOICE_ID_CREDIT
  }
}
